import { useEffect, useState } from "react";
import Reveal from "./Reveal";
import { IMAGES } from "@/constants/site";

type Category = "Tümü" | "Bungalovlar" | "Doğa" | "Restaurant" | "Spa & Wellness";

const FILTERS: Category[] = [
  "Tümü",
  "Bungalovlar",
  "Doğa",
  "Restaurant",
  "Spa & Wellness",
];

interface GalleryItem {
  src: string;
  alt: string;
  category: Exclude<Category, "Tümü">;
}

const ITEMS: GalleryItem[] = [
  { src: IMAGES.night, alt: "Gece aydınlatılmış Düzbağ Life bungalovları", category: "Doğa" },
  { src: IMAGES.sauna, alt: "Düzbağ Life sauna alanı", category: "Spa & Wellness" },
  { src: IMAGES.bedroom, alt: "Düzbağ Life bungalov yatak odası", category: "Bungalovlar" },
  { src: IMAGES.aerialRiver, alt: "Düzbağ Life akarsu, dağ ve bungalov havadan görünüm", category: "Doğa" },
  { src: IMAGES.living, alt: "Ahşap merdivenli yaşam alanı", category: "Bungalovlar" },
  { src: IMAGES.hammam, alt: "Düzbağ Life Türk hamamı", category: "Spa & Wellness" },
  { src: IMAGES.restaurant, alt: "Düzbağ Life Restaurant iç mekan", category: "Restaurant" },
  { src: IMAGES.stairs, alt: "A-frame ahşap merdiven ve asma kat", category: "Bungalovlar" },
  { src: IMAGES.pool, alt: "Düzbağ Life kapalı yüzme havuzu", category: "Spa & Wellness" },
  { src: IMAGES.aerial, alt: "Düzbağ Life A-frame bungalovları havadan görünüm", category: "Bungalovlar" },
  { src: IMAGES.livingStove, alt: "Geniş pencereli sıcak yaşam alanı", category: "Bungalovlar" },
  { src: IMAGES.massage, alt: "Düzbağ Life masaj ve bakım odası", category: "Spa & Wellness" },
  { src: IMAGES.mezzanine, alt: "Asma kattan iç mekan görünümü", category: "Bungalovlar" },
  { src: IMAGES.garden, alt: "Düzbağ Life bahçesi ve A-frame manzarası", category: "Doğa" },
  { src: IMAGES.steam, alt: "Düzbağ Life buhar odası", category: "Spa & Wellness" },
  { src: IMAGES.bath, alt: "Düzbağ Life bungalov banyosu", category: "Bungalovlar" },
];

export default function Gallery() {
  const [active, setActive] = useState<Category>("Tümü");
  const [lightbox, setLightbox] = useState<number | null>(null);

  const filtered =
    active === "Tümü" ? ITEMS : ITEMS.filter((item) => item.category === active);

  useEffect(() => {
    document.body.style.overflow = lightbox !== null ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [lightbox]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (lightbox === null) return;
      if (e.key === "Escape") setLightbox(null);
      if (e.key === "ArrowRight") setLightbox((i) => (i === null ? i : (i + 1) % filtered.length));
      if (e.key === "ArrowLeft")
        setLightbox((i) => (i === null ? i : (i - 1 + filtered.length) % filtered.length));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, filtered.length]);

  return (
    <section id="galeri" className="w-full bg-background-50">
      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-20 md:py-28">
        <Reveal className="text-center max-w-2xl mx-auto">
          <span className="text-secondary-600 text-xs tracking-[0.3em] uppercase">
            Galeri
          </span>
          <h2 className="mt-5 font-heading text-3xl md:text-4xl lg:text-5xl text-foreground-950 leading-[1.15] font-medium">
            GERÇEK DÜZBAĞ LIFE DENEYİMİ
          </h2>
          <p className="mt-5 text-foreground-600 text-sm md:text-base">
            Fotoğraflarda gördüğünüz gerçek Düzbağ Life deneyimi.
          </p>
        </Reveal>

        {/* Filters */}
        <Reveal className="mt-10 flex flex-wrap items-center justify-center gap-2">
          {FILTERS.map((filter) => (
            <button
              key={filter}
              type="button"
              onClick={() => {
                setActive(filter);
                setLightbox(null);
              }}
              className={`px-5 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                active === filter
                  ? "bg-primary-700 text-background-50"
                  : "bg-background-100 text-foreground-700 hover:bg-background-200"
              }`}
            >
              {filter}
            </button>
          ))}
        </Reveal>

        {/* Masonry */}
        <div className="mt-10 columns-1 sm:columns-2 lg:columns-3 gap-4 [column-fill:_balance]">
          {filtered.map((item, index) => (
            <button
              key={`${item.src}-${index}`}
              type="button"
              onClick={() => setLightbox(index)}
              className="mb-4 block w-full rounded-lg overflow-hidden cursor-pointer group relative"
            >
              <img
                src={item.src}
                alt={item.alt}
                title="Düzbağ Life galeri"
                className="w-full h-auto align-bottom transition-transform duration-700 group-hover:scale-[1.03]"
              />
              <span className="absolute inset-0 bg-foreground-950/0 group-hover:bg-foreground-950/30 transition-colors flex items-center justify-center">
                <i className="ri-zoom-in-line text-background-50 text-2xl opacity-0 group-hover:opacity-100 transition-opacity" />
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {lightbox !== null && filtered[lightbox] && (
        <div
          className="fixed inset-0 z-[60] bg-foreground-950/95 flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
        >
          <button
            type="button"
            aria-label="Kapat"
            onClick={() => setLightbox(null)}
            className="absolute top-4 right-4 w-12 h-12 flex items-center justify-center rounded-full bg-background-50/10 text-background-50 hover:bg-background-50/20 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-2xl" />
          </button>
          <button
            type="button"
            aria-label="Önceki"
            onClick={(e) => {
              e.stopPropagation();
              setLightbox((i) => (i === null ? i : (i - 1 + filtered.length) % filtered.length));
            }}
            className="absolute left-3 md:left-6 w-11 h-11 flex items-center justify-center rounded-full bg-background-50/10 text-background-50 hover:bg-background-50/20 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line text-2xl" />
          </button>
          <img
            src={filtered[lightbox].src}
            alt={filtered[lightbox].alt}
            className="max-w-full max-h-[85vh] rounded-lg object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            type="button"
            aria-label="Sonraki"
            onClick={(e) => {
              e.stopPropagation();
              setLightbox((i) => (i === null ? i : (i + 1) % filtered.length));
            }}
            className="absolute right-3 md:right-6 w-11 h-11 flex items-center justify-center rounded-full bg-background-50/10 text-background-50 hover:bg-background-50/20 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-right-line text-2xl" />
          </button>
        </div>
      )}
    </section>
  );
}